import { isValid, parse } from 'date-fns';
import { isEmpty, isPlainObject } from 'lodash';
import { cond, isFunction, memoize, negate, noop } from 'lodash/fp';
import React from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';

import { bankCodes } from '@skippay/core/constants/czechBankCodes';
import { FormattedMessage } from '@skippay/core/i18n/intl';
import { isWorkday } from '@skippay/core/utils/dateUtils';
import { overOr } from '@skippay/core/utils/fp';
import { IBAN_RE, toDigits } from '@skippay/core/utils/iban';

export const pipeValidators = (...validators) => overOr(validators);

export const withErrorMessage = (errorMessage, validator) => (value) =>
	validator(value) && (isFunction(errorMessage) ? errorMessage(value) : errorMessage);

const createInputValidator = memoize((inputType) => {
	const input = document.createElement('input');
	input.type = inputType;
	input.required = true;

	return (value) => {
		input.value = value;
		return input.checkValidity();
	};
});

export const isVoid = (value) =>
	value == null || value === '' || (isPlainObject(value) && isEmpty(value));

const czechBankAccountNthWeights = [1, 2, 4, 8, 5, 10, 9, 7, 3, 6];
const czechBankAccountValidByChecksum = (accountNumberPart) => {
	const sum = Array.from(accountNumberPart)
		.reverse()
		.reduce((acc, digit, idx) => acc + digit * czechBankAccountNthWeights[idx], 0);
	return sum % 11 === 0;
};
const digitsOnlyRe = /^\d+$/;
export const czechBankAccount = (bankAccount) => {
	if (isVoid(bankAccount)) {
		return undefined;
	}
	const { bankCode, firstPart, secondPart } = bankAccount;
	if (
		(bankCode && !digitsOnlyRe.test(bankCode)) ||
		(firstPart && !digitsOnlyRe.test(firstPart)) ||
		(secondPart && !digitsOnlyRe.test(secondPart))
	) {
		return (
			<FormattedMessage
				defaultMessage="Číslo účtu musí obsahovat pouze číslice"
				id="validationError.czechBankAccount.digitsOnly"
			/>
		);
	}
	if (firstPart) {
		if (firstPart.length > 6) {
			return (
				<FormattedMessage
					defaultMessage="Předčíslí čísla účtu přesahuje délku 6 znaků"
					id="validationError.czechBankAccount.firstPart.tooLong"
				/>
			);
		}
		if (!czechBankAccountValidByChecksum(firstPart)) {
			return (
				<FormattedMessage
					defaultMessage="Předčíslí čísla účtu obsahuje chybu"
					id="validationError.czechBankAccount.firstPart.checksum"
				/>
			);
		}
	}
	if (secondPart.length < 2) {
		return firstPart ? (
			<FormattedMessage
				defaultMessage="Druhá část čísla účtu musí mít alespoň 2 znaky"
				id="validationError.czechBankAccount.secondPart.tooShort.hasFirstPart"
			/>
		) : (
			<FormattedMessage
				defaultMessage="Číslo účtu musí mít alespoň 2 znaky"
				id="validationError.czechBankAccount.secondPart.tooShort"
			/>
		);
	}
	if (secondPart.length > 10) {
		return firstPart ? (
			<FormattedMessage
				defaultMessage="Druhá část čísla účtu přesahuje délku 10 znaků"
				id="validationError.czechBankAccount.secondPart.tooLong.hasFirstPart"
			/>
		) : (
			<FormattedMessage
				defaultMessage="Číslo účtu musí přesahuje délku 6 znaků"
				id="validationError.czechBankAccount.secondPart.tooLong"
			/>
		);
	}
	if (!czechBankAccountValidByChecksum(secondPart)) {
		return firstPart ? (
			<FormattedMessage
				defaultMessage="Druhá část čísla účtu obsahuje chybu"
				id="validationError.czechBankAccount.secondPart.checksum.hasFirstPart"
			/>
		) : (
			<FormattedMessage
				defaultMessage="Číslo účtu obsahuje chybu"
				id="validationError.czechBankAccount.secondPart.checksum"
			/>
		);
	}
	if (!bankCode) {
		return (
			<FormattedMessage
				defaultMessage="Chybí kód banky"
				id="validationError.czechBankAccount.bankCode.required"
			/>
		);
	}
	if (!bankCodes[bankCode]) {
		return (
			<FormattedMessage
				defaultMessage="Neplatný kód banky"
				id="validationError.czechBankAccount.bankCode.unknown"
			/>
		);
	}
	return undefined;
};

// exhaustive validation, when needed, can be implemented according to CSSZ
// @see https://www.cssz.cz/web/cz/standardni-kontrola-rodneho-cisla-a-evidencniho-cisla-pojistence
function isValidDate(month, day, year) {
	const date = new Date(year, month - 1, day);
	return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

function verifyCzechPinNumber(rc) {
	const regex = /^\s*(\d\d)(\d\d)(\d\d)[ /]*(\d\d\d)(\d?)\s*$/;
	const matches = rc.match(regex);
	if (!matches) {
		return false;
	}

	const [, year, month, day, ext, c] = matches;

	// the nine-digit registration number assigned until 1954 cannot be verified
	if (c === '') {
		return parseInt(year, 10) < 54;
	}

	// check digit
	let mod = (year + month + day + ext) % 11;
	if (mod === 10) mod = 0;
	if (mod !== parseInt(c, 10)) {
		return false;
	}

	// date check
	const fullYear = parseInt(year, 10) + (parseInt(year, 10) < 54 ? 2000 : 1900);
	let calculatedMonth = parseInt(month, 10);

	// 20, 50 or 70 can be added to the month
	if (calculatedMonth > 70 && fullYear > 2003) {
		calculatedMonth -= 70;
	} else if (calculatedMonth > 50) {
		calculatedMonth -= 50;
	} else if (calculatedMonth > 20 && fullYear > 2003) {
		calculatedMonth -= 20;
	}

	if (!isValidDate(calculatedMonth, parseInt(day, 10), fullYear)) {
		return false;
	}

	return true;
}

export const czechPIN = (value) => {
	return isVoid(value) || verifyCzechPinNumber(value) ? undefined : (
		<FormattedMessage
			defaultMessage="Rodné číslo je chybně zadané"
			id="validationError.format.czechPIN"
		/>
	);
};

const referenceDate = new Date();
export const dateISO = (value) =>
	!isVoid(value) &&
	(typeof value !== 'string' ||
		value.length !== 10 ||
		!isValid(parse(value, 'yyyy-MM-dd', referenceDate))) ? (
		<FormattedMessage
			defaultMessage="Zadané datum je neplatné"
			id="validationError.format.dateISO"
		/>
	) : undefined;

const czechPhoneNumber = /^(\+420)? ?[1-9][0-9]{2} ?[0-9]{3} ?[0-9]{3}$/;
export const phone = (value) =>
	isVoid(value) || czechPhoneNumber.test(value) ? undefined : (
		<FormattedMessage
			defaultMessage="Vámi zadaný telefon má špatný tvar"
			id="validationError.format.phone"
		/>
	);

export const isValidPhone = (value) =>
	isVoid(value) || isValidPhoneNumber(value) ? undefined : (
		<FormattedMessage
			defaultMessage="Vámi zadaný telefon má špatný tvar"
			id="validationError.format.phone"
		/>
	);

export const email = (value) =>
	value && !createInputValidator('email')(value) ? (
		<FormattedMessage
			defaultMessage="Vámi zadaný e-mail má špatný tvar"
			id="validationError.format.email"
		/>
	) : undefined;

export const dateInRange = ({ max, min }) => {
	if (
		process.env.NODE_ENV === 'development' &&
		max !== undefined &&
		min !== undefined &&
		min > max
	) {
		throw new RangeError(
			`"max" boundary must be _lower or equal_ than "min" boundary in "dateInRange" validator.`
		);
	}
	return (value) => {
		if (!isVoid(value)) {
			const valueDate = new Date(value);
			const valid =
				isValid(valueDate) &&
				(max === undefined || max >= valueDate) &&
				(min === undefined || min <= valueDate);
			if (!valid) {
				return (
					<FormattedMessage
						defaultMessage="Datum je mimo povolený rozsah"
						id="validationError.dateInRange"
					/>
				);
			}
		}
		return undefined;
	};
};

export const dateWorkDay = (value) => {
	if (!isVoid(value)) {
		const valueDate = new Date(value);
		const valid = isValid(valueDate) && isWorkday(valueDate);
		return !valid ? (
			<FormattedMessage
				defaultMessage="Zvolte prosím pracovní den"
				id="validationError.dateWorkday"
			/>
		) : undefined;
	}
	return undefined;
};

export const length = ({ max = Number.POSITIVE_INFINITY, min = 0 }) => {
	if (process.env.NODE_ENV === 'development' && min > max) {
		throw new RangeError(
			`"max" boundary must be _lower or equal_ than "min" boundary in "length" validator`
		);
	}
	return (value) => {
		if (!isVoid(value)) {
			const valueLength = Number(value.length);
			if (Number.isNaN(valueLength)) {
				if (process.env.NODE_ENV === 'development') {
					// eslint-disable-next-line no-console
					console.error(
						`Validator 'length' could be used only with values which has a 'length' property.`
					);
				}
			} else if (valueLength > max) {
				return (
					<FormattedMessage
						defaultMessage={`Políčko nesmí obsahovat více než {maxLength, plural,
							one {# znak}
							few {# znaky}
							other {# znaků}}
						`}
						id="validationError.format.length.exceeding"
						values={{ maxLength: max }}
					/>
				);
			} else if (valueLength < min) {
				return (
					<FormattedMessage
						defaultMessage={`Políčko musí obsahovat alespoň {minLength, plural,
							one {# znak}
							few {# znaky}
							other {# znaků}}
						`}
						id="validationError.format.length.insufficient"
						values={{ minLength: min }}
					/>
				);
			}
		}
		return undefined;
	};
};

/**
 * Validates International Bank Account Number (IBAN). Assumes input without whitespace.
 * @param {object} options
 * @property {string[]} [allowedCountryCodes] - according to the ISO-3166-1, must be uppercase
 * @see https://cs.wikipedia.org/wiki/ISO_3166-1
 * @return {React.ReactNode | undefined}
 */
export const iban = ({ allowedCountryCodes } = {}) =>
	pipeValidators(length({ max: 34, min: 15 }), (value) => {
		if (isVoid(value)) {
			return undefined;
		}
		const match = value.match(IBAN_RE);
		if (match) {
			const [, countryCode, checksum, bankNumber] = match;
			if (allowedCountryCodes && !allowedCountryCodes.includes(countryCode)) {
				return (
					<FormattedMessage
						defaultMessage={'Kód země "{countryCode}" není podporován.'}
						id="validationError.iban.unsupportedCountry"
						values={{ countryCode: value.slice(0, 2) }}
					/>
				);
			}
			const valid =
				Number(BigInt(toDigits(`${bankNumber}${countryCode}${checksum}`)) % BigInt(97)) === 1;
			if (valid) {
				return undefined;
			}
		}
		return (
			<FormattedMessage defaultMessage="Zadaný kód IBAN není platný." id="validationError.iban" />
		);
	});

export const numberInRange = ({
	max = Number.POSITIVE_INFINITY,
	min = Number.NEGATIVE_INFINITY,
}) => {
	if (process.env.NODE_ENV === 'development' && min > max) {
		throw new RangeError(
			`"max" boundary must be _lower or equal_ than "min" boundary in "numberInRange" validator`
		);
	}
	return (value) => {
		if (!isVoid(value)) {
			const valueNum = Number(value);
			if (Number.isNaN(valueNum)) {
				if (process.env.NODE_ENV === 'development') {
					// eslint-disable-next-line no-console
					console.error(
						`Validator 'numberInRange' could be used only with values which are _number_.`
					);
				}
			} else if (valueNum > max) {
				return (
					<FormattedMessage
						defaultMessage="Zadaná hodnota je příliš vysoká, povolené maximum je {max}"
						id="validationError.format.numberInRange.exceeding"
						values={{ max }}
					/>
				);
			} else if (valueNum < min) {
				return (
					<FormattedMessage
						defaultMessage="Zadaná hodnota je příliš nízká, povolené minimum je {min}"
						id="validationError.format.numberInRange.insufficient"
						values={{ min }}
					/>
				);
			}
		}
		return undefined;
	};
};

const passwordRequiredChars = cond([
	[isVoid, noop],
	[
		negate(RegExp.prototype.test.bind(/[a-z]/)),
		() => (
			<FormattedMessage
				defaultMessage="Heslo musí obsahovat alespoň jedno malé písmeno"
				id="validationError.passwordStrength.missingLowerLetter"
			/>
		),
	],
	[
		negate(RegExp.prototype.test.bind(/[A-Z]/)),
		() => (
			<FormattedMessage
				defaultMessage="Heslo musí obsahovat alespoň jedno velké písmeno"
				id="validationError.passwordStrength.missingUpperLetter"
			/>
		),
	],
	[
		negate(RegExp.prototype.test.bind(/\d/)),
		() => (
			<FormattedMessage
				defaultMessage="Heslo musí obsahovat alespoň jedno číslo"
				id="validationError.passwordStrength.missingNumber"
			/>
		),
	],
]);
export const passwordStrength = (minLength = 8) =>
	pipeValidators(passwordRequiredChars, length({ min: minLength }));

export const required = (value) =>
	isVoid(value) ? (
		<FormattedMessage defaultMessage="Toto pole je povinné." id="validationError.required" />
	) : undefined;

const fancyLetterPattern = /[\u{1D400}-\u{1D7FF}]/u;
export const notFormattedFont = (value) =>
	isVoid(value) || !fancyLetterPattern.test(value) ? undefined : (
		<FormattedMessage
			defaultMessage="Políčko nesmí obsahovat nepovolené znaky, jako je například formátované písmo."
			id="validationError.format.fancyLetter"
		/>
	);

/**
 * Validates that checkbox is checked, yields `required` error if not.
 * @param {any} value
 * @return {string | undefined}
 */
export const checked = (value) => (value === true ? undefined : required(undefined));

export const url = (value) =>
	value && !createInputValidator('url')(value) ? (
		<FormattedMessage
			defaultMessage="Políčko neobsahuje platnou url"
			id="validationError.format.url"
		/>
	) : undefined;

const reScheme = /^https?:\/\//;
export const urlShort = (value) =>
	(value && url(reScheme.test(value) ? value : `http://${value}`)) || undefined;
